/**
 * Server state, in one place.
 *
 * TWO CONVENTIONS, both enforced by test/queries.test.ts:
 *
 * 1. A query key is [resource] or [resource, param]. Never a tenant id -- the
 *    session decides the tenant, so a per-tenant cache key would be a lie that
 *    happens to work, and would stop working the day someone adds account
 *    switching.
 * 2. Every mutation names what it invalidates, HERE, not at its call site. A
 *    mutation whose effects reach another view (a disconnect that turns
 *    feature.ebay off; a settings write that changes the timezone the runtime
 *    reads) invalidates that view too, and the map is where a reviewer can see
 *    whether it does.
 */
import { QueryClient } from '@tanstack/react-query';
import { ADMIN_API, AUTH_API, ApiError } from './lib/api';
import { jobsRefetchInterval } from './lib/jobDisplay';
import type { JobsResponse, MetricRange } from './lib/types';

export const queryKeys = {
  config: ['config'] as const,
  me: ['me'] as const,
  overview: ['overview'] as const,
  staff: ['staff'] as const,
  users: ['users'] as const,
  devices: ['devices'] as const,
  sessions: ['sessions'] as const,
  locations: ['locations'] as const,
  lists: ['lists'] as const,
  settings: ['settings'] as const,
  sheet: ['sheet'] as const,
  ebay: ['ebay'] as const,
  jobs: ['jobs'] as const,
  metrics: (range: MetricRange) => ['metrics', range] as const,
} as const;

/**
 * mutation name -> the query keys it makes stale.
 *
 * The non-obvious rows are the ones worth reviewing:
 *  - ebayDisconnect touches `me` and `overview` because the merged handler
 *    turns feature.ebay OFF in the same action (api/src/adminRoutes.ts:1254),
 *    so every feature-gated control in the shell is now wrong.
 *  - putSettings touches `me` because `timezone` feeds plan 2a's projection
 *    and therefore the runtime record `me` reports.
 *  - runAction touches `overview` as well as `jobs`: the Start view renders
 *    "last job per type" from the same rows.
 */
export const INVALIDATES: Record<string, readonly (readonly string[])[]> = {
  createStaff: [queryKeys.staff],
  updateStaff: [queryKeys.staff],
  inviteUser: [queryKeys.users],
  updateUser: [queryKeys.users],
  pairToken: [queryKeys.devices],
  revokeDevice: [queryKeys.devices],
  revokeSession: [queryKeys.sessions],
  createLocation: [queryKeys.locations],
  updateLocation: [queryKeys.locations],
  deleteLocation: [queryKeys.locations],
  createList: [queryKeys.lists],
  renameList: [queryKeys.lists],
  reorderLists: [queryKeys.lists],
  deleteList: [queryKeys.lists],
  putSettings: [queryKeys.settings, queryKeys.me],
  patchFeatures: [queryKeys.me, queryKeys.overview, queryKeys.ebay],
  googleFinish: [queryKeys.sheet, queryKeys.overview],
  connectSheet: [queryKeys.sheet, queryKeys.overview],
  validateSheet: [queryKeys.sheet, queryKeys.overview],
  createSheetFromTemplate: [queryKeys.sheet, queryKeys.overview],
  ebayFinish: [queryKeys.ebay, queryKeys.overview, queryKeys.me],
  ebayDisconnect: [queryKeys.ebay, queryKeys.overview, queryKeys.me],
  runAction: [queryKeys.jobs, queryKeys.overview],
};

/**
 * The Start view's one request (task-8-brief.md Step 1: "GET
 * /admin-api/overview returns everything, so the view issues ONE query").
 * Exported as a factory for the same reason Boot.tsx's `meQueryOptions` is:
 * asserting its `queryKey` costs no renderer.
 */
export function overviewQueryOptions() {
  return {
    queryKey: queryKeys.overview,
    queryFn: () => ADMIN_API.overview(),
  };
}

/**
 * The Staff view's four lists (task-9-brief.md Step 1). Factories for the same
 * reason `overviewQueryOptions` is one: asserting the key costs no renderer,
 * and three of these four are the surface where a wrong key would show one
 * tenant's people under another tenant's session.
 *
 * `devicesQueryOptions` caches the RAW snake_case rows GET /auth/devices
 * returns (plan 3c interface D19). Mapping them inside this queryFn would put
 * the translation back in shared infrastructure, which is exactly what the
 * brief forbids -- it belongs at the one call site that renders the table, in
 * views/Staff.tsx, where it is visible.
 */
export function staffQueryOptions() {
  return { queryKey: queryKeys.staff, queryFn: () => ADMIN_API.listStaff() };
}

export function usersQueryOptions() {
  return { queryKey: queryKeys.users, queryFn: () => ADMIN_API.listUsers() };
}

export function devicesQueryOptions() {
  return { queryKey: queryKeys.devices, queryFn: () => AUTH_API.listDevices() };
}

export function sessionsQueryOptions() {
  return { queryKey: queryKeys.sessions, queryFn: () => AUTH_API.listSessions() };
}

/**
 * `/stammdaten`'s three lists (task-10-brief.md Steps 1-3). Factories for the
 * same reason the Staff view's four are: asserting the key costs no
 * renderer, and `locations`/`lists` are the surface where reorder's optimistic
 * `setQueryData` writes directly (lib/masterDataView.ts's `reorderListsAction`),
 * so the key it writes through must be the exact one this factory reads.
 */
export function locationsQueryOptions() {
  return { queryKey: queryKeys.locations, queryFn: () => ADMIN_API.listLocations() };
}

export function listsQueryOptions() {
  return { queryKey: queryKeys.lists, queryFn: () => ADMIN_API.listLists() };
}

export function settingsQueryOptions() {
  return { queryKey: queryKeys.settings, queryFn: () => ADMIN_API.getSettings() };
}

/**
 * `/tabelle`'s one query (Task 11). A factory for the same reason the others
 * are: asserting the key costs no renderer, and this is the key
 * `INVALIDATES.googleFinish` / `connectSheet` / `validateSheet` /
 * `createSheetFromTemplate` all write through -- a connect that refreshed a
 * DIFFERENT key would leave the view reading "nicht verbunden" over a binding
 * that exists.
 */
export function sheetQueryOptions() {
  return { queryKey: queryKeys.sheet, queryFn: () => ADMIN_API.getSheet() };
}

/**
 * `/ebay`'s one query (Task 12). A factory for the same reason the others are,
 * and it carries more weight than most: this single response holds BOTH the
 * connection row and `features.ebay`, so the confirmation gate and the flag it
 * unlocks are read from one consistent answer rather than from two requests
 * that can disagree. It is also the key `INVALIDATES.ebayFinish` /
 * `ebayDisconnect` / `patchFeatures` all write through -- a finish that
 * refreshed a DIFFERENT key would leave the view showing "nicht verbunden"
 * over a stored connection, and the operator would never reach the gate.
 */
export function ebayQueryOptions() {
  return { queryKey: queryKeys.ebay, queryFn: () => ADMIN_API.getEbay() };
}

/**
 * `/jobs`'s two queries (Task 13).
 *
 * THE POLL LIVES HERE, NOT IN THE VIEW, and that is the point of putting these
 * behind factories at all: `refetchInterval` is a decision -- "5 s only while a
 * row is running or queued" (task-13-brief.md Step 3) -- and a decision written
 * as an inline arrow in JSX is a decision no Node test can reach. As a factory,
 * `jobsQueryOptions().refetchInterval` can be CALLED with a fake query in
 * test/jobsView.test.ts, so "the view actually polls a busy table and actually
 * stops on a quiet one" is asserted rather than assumed. The decision itself is
 * `jobsRefetchInterval` in lib/jobsView.ts; this is the wiring, and both halves
 * are pinned.
 *
 * `metricsQueryOptions` is keyed PER RANGE (`queryKeys.metrics(range)`): the
 * three windows are three different answers, and one shared key would show the
 * 24 h bundle under a "30 Tage" heading for as long as the cache lives.
 */
export function jobsQueryOptions() {
  return {
    queryKey: queryKeys.jobs,
    queryFn: () => ADMIN_API.jobs(),
    refetchInterval: (query: { state: { data: JobsResponse | undefined } }) =>
      jobsRefetchInterval(query.state.data),
  };
}

export function metricsQueryOptions(range: MetricRange) {
  return {
    queryKey: queryKeys.metrics(range),
    queryFn: () => ADMIN_API.metrics(range),
    // A metrics bundle is cached 60 s on the server (adminMetrics.ts) and the
    // numbers move by the minute; the shell's 30 s default is the right shape
    // and is deliberately left alone rather than re-stated here.
  };
}

/**
 * `INVALIDATES`, APPLIED -- the one function every mutation call site goes
 * through, so "what does this refresh?" is answered by the map above and never
 * by an ad-hoc `invalidateQueries` next to a button (this module's rule 2).
 *
 * A missing row is an empty no-op rather than a throw: `INVALIDATES` is typed
 * `Record<string, …>`, so under `noUncheckedIndexedAccess` the lookup is
 * `| undefined` even for rows that always exist, and a mutation renamed out
 * from under a call site must not take the whole view down with it. It DOES
 * mean a typo'd name silently refreshes nothing -- which is why the tests
 * assert the real names, not just that the helper runs.
 */
export function invalidateMutation(queryClient: QueryClient, mutation: string): void {
  for (const key of INVALIDATES[mutation] ?? []) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * A 401 MID-ACTION IS NOT A DEAD LETTER (final review I-1).
 *
 * Before this, `lib/bootState.ts:72` was the ONLY consumer of a 401 anywhere in
 * `src/`. A session that expired between loading a view and pressing a button
 * produced `{ error: 'unauthenticated' }` -> an `ApiError` the retry predicate
 * below correctly refuses to retry -> the panel's own `catch` -> the generic
 * "Der Vorgang ist fehlgeschlagen." -- rendered UNDER a shell still showing the
 * operator's email, their tenant name, the billing banner and the full
 * navigation. Recovery was entirely opportunistic: a window focus, or a `useMe`
 * consumer mounting after `staleTime` elapsed. On a focused tab where the
 * operator simply retries the same button, the shell could claim "signed in"
 * indefinitely. That was the one place this SPA rendered something untrue.
 *
 * THE 401 NOW DRIVES THE SESSION STATE ITSELF. Invalidating `me` re-asks the
 * server who this is; `me` is an ACTIVE query in every authenticated render, so
 * the invalidation refetches immediately, the refetch answers 401 again,
 * `chooseBootScreen` sees `me.isError` with status 401, and `Boot` swaps the
 * whole shell for the login screen. The operator stops being told a session
 * exists that does not.
 *
 * WHY ASK THE SERVER RATHER THAN CLEARING THE CACHE OUTRIGHT: this SPA's own
 * standard everywhere else is that it renders what the server said, never what
 * it inferred. If `/admin-api/me` still answers 200, then the 401 came from
 * something other than the session and tearing the shell down would be the new
 * lie. Invalidation is the version of this fix that cannot itself misreport.
 *
 * The copy half lives in components/ErrorNotice.tsx (`error.unauthenticated`).
 * Both halves are needed: an invalidation alone leaves the last frame saying
 * "Der Vorgang ist fehlgeschlagen.", and copy alone leaves a signed-out shell
 * rendering a signed-in identity.
 */
export function reportAuthFailure(queryClient: QueryClient, error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return reportAuthStatus(queryClient, error.status);
}

/**
 * The same thing for the one route that never mints an `ApiError`: the DATEV
 * export reads its response through `adminFetchRaw` (the success path is a
 * windows-1252 FILE, so it cannot go through `adminFetch`'s JSON parse), which
 * means a 401 there arrives as a plain `Response` and lib/api.ts's taxonomy
 * never sees it. Status rather than kind, for that one caller.
 */
export function reportAuthStatus(queryClient: QueryClient, status: number): boolean {
  if (status !== 401) return false;
  void queryClient.invalidateQueries({ queryKey: queryKeys.me });
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // A 401 is terminal (lib/api.ts): the API already cleared the cookie,
        // so retrying is a loop against a session that no longer exists.
        // 403/404/409 are answers, not failures. Only 5xx and network get a
        // second chance.
        retry: (failureCount, error) => {
          const kind = (error as { kind?: string }).kind;
          if (kind === 'network' || kind === 'upstream') return failureCount < 2;
          return false;
        },
        staleTime: 30_000,
        // The Admin is a desk tool left open in a tab; refetching what changed
        // while it was in the background is the whole reason this dependency
        // is here.
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}
